import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getDealers } from "@insite/client-framework/Services/DealerService";
import siteMessage from "@insite/client-framework/SiteMessage";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import { DealerModel, ImageModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Button from "@insite/mobius/Button";
import { DynamicDropdownProps, OptionObject } from "@insite/mobius/DynamicDropdown";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import UcsReCaptcha from "@ucs/Components/UcsReCaptcha";
import ucsGuestQuoteRequest, { QuoteRequestModel } from "@ucs/Handlers/UcsGuestQuoteRequest";
import { formatPhoneNumber } from "@ucs/Helpers/Formatting";
import {
    validateCompanyNameField,
    validateEmailField,
    validateFirstNameField,
    validateLastNameField,
    validatePhoneField,
    validateQuantityField,
} from "@ucs/Helpers/FormFieldsValidation";
import TextField, { TextFieldPresentationProps } from "@ucs/MobiusOverrides/TextField/TextField";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@ucs/Overrides/Components/ProductPartNumbers";
import customLoadDealers from "@ucs/Store/Custom/CustomLoadDealers";
import { getDealersDropdown, getGeoPermission } from "@ucs/Store/CustomSelectors";
import { CustomState } from "@ucs/Store/Reducers";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { RefObject } from "react";
import { FC, useContext, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomState) => {
    return {
        isGeoPermissionDenied: getGeoPermission(state),
        isAuthenticated: state.context.session!.isAuthenticated,
        state,
        dealersDropdown: getDealersDropdown(state),
    };
};

const mapDispatchToProps = {
    ucsGuestQuoteRequest,
    customLoadDealers,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
};

interface OwnProps {
    isModalOpen: boolean;
    toggleModal: Function;
    extendedStyles?: GuestQuoteRequestFormStyles;
    redirectOnSubmit :  LinkFieldValue;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface GuestQuoteRequestFormStyles {
    formModal?: ModalPresentationProps;
    wrapper?: InjectableCss;
    productSection?: InjectableCss;
    img?: LazyImageProps;
    descriptionSection?: InjectableCss;
    brandLabel?: TypographyProps;
    productName?: TypographyProps;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    formGroup?: InjectableCss;
    textFieldStyles?: TextFieldPresentationProps;
    nearestLocationDropdown?: DynamicDropdownProps;
    messageTextArea?: TextAreaProps;
    submitButtonWrapper?: InjectableCss;
    locationSelect?: SelectPresentationProps;
}

export const guestQuoteRequestFormStyles: GuestQuoteRequestFormStyles = {
    formModal: {
        size: 800,
        cssOverrides: {
            modalContent: css`
                padding: 0 20px 10px;
                background-color: ${UCSColors.gray08};
            `,
            modalTitle: css`
                padding: 20px;
                border-bottom: none;
                background-color: ${UCSColors.gray08};
            `,
            titleButton: css`
                width: 20px;
                height: 20px;
                min-height: 20px;
                padding: 0;
                position: relative;
                background-color: transparent !important;
                background: transparent !important;
                border: none;
                & span {
                    position: absolute !important;
                    top: 0 !important;
                    left: -1px;
                    width: 18px;
                    height: 18px;
                }
                & svg {
                    width: 18px;
                    height: 18px;
                }
                &:hover {
                    background-color: transparent !important;
                    background: transparent !important;
                    border: none;
                }
            `,
            modalContainer: css`
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            margin-top: 0;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            height: 90%;
                            padding-top: 60px;
                        `,
                        css`
                            margin-top: 0;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            height: 100%;
                        `,
                        css`
                            margin-top: 0;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            height: 100%;
                        `,
                    ]);
                }}
            `,
            modalBody: css`
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            height: 100vh;
                            width: 100vw;
                            min-height: 350px;
                            box-shadow: ${theme?.shadows[3]};
                        `,
                        css`
                            height: auto;
                            max-height: calc(100% - 40px);
                            margin: 25px;
                            max-width: 540px;
                            box-shadow: ${theme?.shadows[3]};
                        `,
                        css`
                            height: auto;
                            max-height: calc(100% - 90px);
                            margin: 45px;
                            max-width: 660px;
                            box-shadow: ${theme?.shadows[3]};
                        `,
                    ]);
                }}
            `,
        },
    },
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            form > * {
                margin-bottom: 15px;
            }
        `,
    },
    productSection: {
        css: css`
            width: 100%;
            display: flex;
            gap: 10px;
            align-items: flex-start;
            margin-bottom: 20px;
            background-color: #fff;
            padding: 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        align-items: stretch;
                    `,
                    css`
                        align-items: stretch;
                    `,
                ]);
            }}
        `,
    },
    img: {
        width: "120px",
        height: "120px",
        css: css`
            box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.2);
            & img {
                height: 100%;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 100px;
                        min-width: 100px;
                        min-height: 100px;
                        height: auto;
                        margin-right: 3px;
                    `,
                    css`
                        width: 100px;
                        min-width: 100px;
                        min-height: 100px;
                        height: auto;
                        margin-right: 3px;
                    `,
                    css`
                        width: 100px;
                        height: 100px;
                    `,
                ]);
            }}
        `,
    },
    productName: {
        size: 18,
        color: UCSColors.gray00,
        weight: 500,
        as: "p",
        css: css`
            margin-bottom: 8px;
            line-height: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 16px;
                        margin-bottom: 6px;
                    `,
                    css`
                        font-size: 16px;
                        margin-bottom: 6px;
                    `,
                ]);
            }}
        `,
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                min-width: 200px;
            `,
        },
        erpNumberGridItem: {
            css: css`
                color: ${UCSColors.gray03};
                font-weight: 400;
                font-size: 14px;
                ${({ theme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            font-size: 13px;
                        `,
                        css`
                            font-size: 13px;
                        `,
                    ]);
                }}
            `,
        },
        erpNumberLabelText: {
            weight: 400,
            size: 14,
            css: css`
                margin-right: 4px;
                margin-bottom: 6px;
                min-width: fit-content;
            `,
        },
        erpNumberValueText: {
            css: css`
                min-width: fit-content;
            `,
        },
        customerNameGridItem: {
            css: css`
                color: ${UCSColors.gray03};
                font-weight: 400;
                font-size: 14px;
            `,
        },
        customerNameLabelText: {
            variant: "p",
            weight: 400,
            size: 14,
            css: css`
                margin-right: 4px;
                margin-bottom: 6px;
            `,
        },
        manufacturerItemGridItem: {
            css: css`
                color: ${UCSColors.gray03};
                font-weight: 400;
                font-size: 14px;
            `,
        },
        manufacturerItemLabelText: {
            variant: "p",
            weight: 400,
            size: 14,
            css: css`
                margin-right: 4px;
                margin-bottom: 6px;
            `,
        },
    },
    descriptionSection: {
        css: css`
            margin-right: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        margin-right: 0;
                    `,
                    css`
                        margin-right: 0;
                    `,
                    css`
                        margin-right: 15px;
                    `,
                ]);
            }}
            @media only screen and (max-width: 400px) {
                max-width: 57%;
            }
        `,
    },
    brandLabel: {
        weight: 700,
        transform: "uppercase",
        css: css`
            font-size: 13px;
            color: ${UCSColors.gray04} !important;
            letter-spacing: 0.1em;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                    `,
                    css`
                        font-size: 12px;
                    `,
                ]);
            }}
        `,
    },
    messageTextArea: {
        css: css`
            resize: vertical;
        `,
    },
    submitButtonWrapper: {
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding-bottom: 30px;
                    `,
                ]);
            }}
        `,
    },
    formGroup: {
        css: css`
            width: 100%;
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 100%;
                        gap: 15px;
                    `,
                    css`
                        grid-template-columns: 100%;
                        gap: 15px;
                    `,
                ]);
            }}
        `,
    },
    textFieldStyles: {
        labelProps: {
            size: 13,
            weight: 700,
            css: css`
                line-height: 16px;
                letter-spacing: 0.1em;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 6px !important;
            `,
        },
    },
};

const GuestQuoteRequestForm: FC<Props> = ({
    extendedStyles,
    isModalOpen,
    toggleModal,
    ucsGuestQuoteRequest,
    customLoadDealers,
    state,
    isGeoPermissionDenied,
    dealersDropdown,
    redirectOnSubmit,
    history,
    validateReCaptcha,
    ...otherProps
}) => {
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();
    const [styles] = useState(() => mergeToNew(guestQuoteRequestFormStyles, extendedStyles));
    const [formLoaded, setFormLoaded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [companyName, setCompanyName] = useState("");
    const [companyErrorMessage, setCompanyErrorMessage] = useState("");
    const [firstName, setFirstName] = useState("");
    const [firstNameErrorMessage, setFirstNameErrorMessage] = useState("");
    const [lastName, setLastName] = useState("");
    const [lastNameErrorMessage, setLastNameErrorMessage] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [phoneErrorMessage, setPhoneErrorMessage] = useState("");
    const [emailAddres, setEmailAddress] = useState("");
    const [emailErrorMessage, setEmailErrorMessage] = useState("");
    const [quantityErrorMessage, setQuantityErrorMessage] = useState("");
    const [message, setMessage] = useState("");

    const getImageUrl = () => {
        if (product && product.images && product.images.length > 0) {
            let img = product.images.find((image: ImageModel) => image.name.toLowerCase().includes("_swatch"));
            if (!img) {
                img = product.images.find((image: ImageModel) => image.name.toLowerCase().includes("_alt1"));
            }
            if (!img) {
                img = product.images.find((image: ImageModel) => image.name.toLowerCase().includes("_alt2"));
            }
            if (img) {
                return img.smallImagePath || img.mediumImagePath;
            }
        }
        return product.smallImagePath || product.mediumImagePath;
    };

    const getUom = () => {
        let label = "";
        if (product && product.unitOfMeasures) {
            const unit = product.unitOfMeasures.find(u => u.unitOfMeasure === productContext.productInfo.unitOfMeasure);
            label = unit?.description || "";
        }
        return label;
    };

    const messagePlaceholder = translate(
        "Please feel free to include any additional order information, products you would like quoted, or questions you have for us. We look forward to responding to your request!",
    );
    const toasterContext = useContext(ToasterContext);
    const productContext: ProductContextModel = useContext(ProductContext);
    const product: ProductModel = productContext.product;
    const productSku: string = product.productNumber;
    const productName: string = product.detail?.name || product.productTitle;
    const productImage: string = getImageUrl();
    const [quantity, setQuantity] = useState(`${productContext.productInfo.qtyOrdered}`);
    const [uom, setUom] = useState(getUom());
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [selectedLocation, setSelectedLocation] = useState("");
    const [locationError, setLocationError] = useState<React.ReactNode>("");

    React.useEffect(() => {
        setFormLoaded(true);
    }, []);

    React.useEffect(() => {
        isModalOpen && setFormLoaded(true);
    }, [isModalOpen]);

    React.useEffect(() => {
        setQuantity(`${productContext.productInfo.qtyOrdered}`);
        setUom(getUom());
    }, [productContext.productInfo]);

    React.useEffect(() => {
        if (formLoaded && typeof quantity !== "undefined") {
            validateQuantity();
        }
    }, [quantity]);

    React.useEffect(() => {
        if (formLoaded && typeof companyName !== "undefined") {
            if (companyName!.length === 0 || companyName!.length >= 2) {
                validateCompany();
            }
        }
    }, [companyName]);

    React.useEffect(() => {
        if (formLoaded && typeof firstName !== "undefined") {
            if (firstName!.length === 0 || firstName!.length >= 2) {
                validateFirstName();
            }
        }
    }, [firstName]);

    React.useEffect(() => {
        if (formLoaded && typeof lastName !== "undefined") {
            if (lastName!.length === 0 || lastName!.length >= 2) {
                validateLastName();
            }
        }
    }, [lastName]);

    React.useEffect(() => {
        if (formLoaded && typeof emailAddres !== "undefined") {
            if (emailAddres!.length === 0 || emailAddres!.length > 4) {
                validateEmail();
            }
        }
        customLoadDealers({} as any);
    }, [emailAddres]);

    const getSelectedLocation=()=>{      
        if (isGeoPermissionDenied === "No") {
            setSelectedLocation(getCookie("SelectedLocation") || "");
        } else {
            setSelectedLocation("");
        }        
    }

    React.useEffect(() => {
        const updateSelectedLocationFromCookie = () => {
            if (isGeoPermissionDenied === "No") {
                setSelectedLocation(getCookie("SelectedLocation") || "");
            } else {
                setSelectedLocation("");
            }
        };

        updateSelectedLocationFromCookie();

        // Listen for storage events (cross-tab)
        const onStorage = (event: StorageEvent) => {
            if (event.key === "SelectedLocation") {
                updateSelectedLocationFromCookie();
            }
        };
        window.addEventListener("storage", onStorage);

        // Poll for same-tab cookie changes
        let lastCookie = getCookie("SelectedLocation");
        const interval = setInterval(() => {
            const currentCookie = getCookie("SelectedLocation");
            if (currentCookie !== lastCookie) {
                lastCookie = currentCookie;
                updateSelectedLocationFromCookie();
            }
        }, 1000);

        return () => {
            window.removeEventListener("storage", onStorage);
            clearInterval(interval);
        };
    }, [isGeoPermissionDenied]);

    const clearForm = () => {
        setFormLoaded(false);
        setLocationError("");
        getSelectedLocation();
        setCompanyName("");
        setCompanyErrorMessage("");
        setFirstName("");
        setFirstNameErrorMessage("");
        setLastName("");
        setLastNameErrorMessage("");
        setPhoneNumber("");
        setPhoneErrorMessage("");
        setEmailAddress("");
        setEmailErrorMessage("");
        setMessage("");
    };

    const modalCloseHandler = () => {
        clearForm();
        toggleModal();
    };

    const validateQuantity = () => {
        const errorMessage = validateQuantityField(quantity);
        setQuantityErrorMessage(errorMessage);
        return !errorMessage;
    };

    const validateCompany = () => {
        const errorMessage = validateCompanyNameField(companyName);
        setCompanyErrorMessage(errorMessage);
        return !errorMessage;
    };

    const validateFirstName = () => {
        const errorMessage = validateFirstNameField(firstName);
        setFirstNameErrorMessage(errorMessage);
        return !errorMessage;
    };

    const validateLastName = () => {
        const errorMessage = validateLastNameField(lastName);
        setLastNameErrorMessage(errorMessage);
        return !errorMessage;
    };

    const validatePhone = () => {
        const phoneFormatted: string = formatPhoneNumber(phoneNumber);
        setPhoneNumber(phoneFormatted);
        const errorMessage = validatePhoneField(phoneFormatted);
        setPhoneErrorMessage(errorMessage);
        return !errorMessage;
    };

    const validateEmail = () => {
        const errorMessage = validateEmailField(emailAddres);
        setEmailErrorMessage(errorMessage);
        return !errorMessage;
    };

     const validateLocation = () => {
        const errorMessage = !selectedLocation ? translate("Please select a nearest location.") : "";
        setLocationError(errorMessage);
        return !errorMessage;
    };

    const qtyKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const quantityChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQuantity(event.target.value);
    };

    const companyChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCompanyName(event.target.value);
    };

    const firstNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFirstName(event.target.value);
    };

    const lastNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLastName(event.target.value);
    };

    const emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailAddress(event.target.value);
    };

    const emailBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        validateEmail();
    };

    const phoneChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPhoneNumber(event.target.value);
    };

    const phoneBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        validatePhone();
    };

    const messageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
    };

    const populateModel = () => {
        const model: QuoteRequestModel = {
            firstName,
            lastName,
            companyName,
            phoneNumber,
            emailAddres,
            message,
            productSku,
            uom,
            quantity: +quantity,
            productName: product.productTitle,
            location: selectedLocation || "",
            emailTo: getCookie("SalesEmailCookie") || "info@ucfp.com",
            topic: (siteMessage("Request_Quote_Topic") as string) || "Guest quote request",
        };
        return model;
    };

    const validateForm = async () => {
        const validationResult: boolean[] = [
            validateQuantity(),
            validateLocation(),
            validateCompany(),
            validateFirstName(),
            validateLastName(),
            validatePhone(),
            validateEmail(),
        ];

        if (!validationResult.includes(false)) {
            const isReCaptchaValid = await validateReCaptcha({ location: "GuestQuoteRequest", recaptchaRef });
            if (!isReCaptchaValid) return;
            setIsSubmitting(true);
            const quoteRequestModel = populateModel();

            ucsGuestQuoteRequest({
                quoteRequestModel,
                onSuccess: () => {
                    if(redirectOnSubmit.value){                        
                        history.push(redirectOnSubmit.value);
                    }  
                    else{
                        toasterContext.addToast({
                            body: siteMessage("Request_Quote_Success"),
                            messageType: "success",
                        });
                        modalCloseHandler();
                    }                    
                    setIsSubmitting(false);                    
                },
                onError: () => {
                    toasterContext.addToast({
                        body: siteMessage("Request_Quote_Failure"),
                        messageType: "danger",
                    });
                    setIsSubmitting(false);
                    modalCloseHandler();
                },
                onComplete() {
                    // "this" is targeting the object being created, not the parent SFC
                     
                    // this.onSuccess?.();
                },
            });
        } else {
            toasterContext.addToast({
                body: siteMessage("Request_Quote_ValidationError"),
                messageType: "danger",
            });
        }
    };

    const listChangeHandler = (value?: string) => {
        setSelectedLocation(value || "");
        const errorMessage = !value ? translate("Please select a nearest location.") : "";
        setLocationError(errorMessage);
    };

    useEffect(() => {
        const locationDropdownData = dealersDropdown.dealers || [];
        const locationOptions = locationDropdownData.map((item: DealerModel) => ({
            optionValue: `${item.name}, ${item.state}`,
            optionText: `${item.name}, ${item.state}`,
        }));
        setOptions(locationOptions);
    }, [isGeoPermissionDenied, dealersDropdown]);

    return (
        <Modal {...styles.formModal} headline="Request a Quote" isOpen={isModalOpen} handleClose={modalCloseHandler}>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.productSection}>
                    <LazyImage {...styles.img} src={productImage} altText="product image" />
                    <StyledWrapper {...styles.descriptionSection}>
                        {product.brand && <Typography {...styles.brandLabel}>{product.brand.name}</Typography>}
                        <Typography {...styles.productName}>{productName}</Typography>

                        <Typography>{product.detail?.productCode}</Typography>

                        <ProductPartNumbers
                            productNumber={productSku}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                    </StyledWrapper>
                </StyledWrapper>

                <form id="quoteRequestForm" noValidate>
                    <StyledWrapper {...styles.formGroup}>
                        <TextField
                            {...styles.textFieldStyles}
                            label="Company"
                            required={true}
                            placeholder=""
                            value={companyName}
                            error={companyErrorMessage}
                            onChange={companyChangeHandler}
                            autoComplete="off"
                            spellCheck={false}
                            autoCorrect="off"
                        />
                        <TextField
                            {...styles.textFieldStyles}
                            label={`${translate("Quantity")} (${uom})`}
                            required={true}
                            value={quantity}
                            error={quantityErrorMessage}
                            onKeyPress={qtyKeyPressHandler}
                            onChange={quantityChangeHandler}
                            autoComplete="off"
                            spellCheck={false}
                            type="number"
                            autoCorrect="off"
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.formGroup}>
                        <TextField
                            {...styles.textFieldStyles}
                            label="First Name"
                            required={true}
                            placeholder=""
                            value={firstName || ""}
                            error={firstNameErrorMessage}
                            onChange={firstNameChangeHandler}
                            autoComplete="off"
                            spellCheck={false}
                            autoCorrect="off"
                        />

                        <TextField
                            {...styles.textFieldStyles}
                            label="Last name"
                            required={true}
                            placeholder=""
                            value={lastName || ""}
                            error={lastNameErrorMessage}
                            onChange={lastNameChangeHandler}
                            autoComplete="off"
                            spellCheck={false}
                            autoCorrect="off"
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.formGroup}>
                        <TextField
                            {...styles.textFieldStyles}
                            label="Phone number"
                            required={true}
                            placeholder="_ _ _ - _ _ _ - _ _ _ _"
                            value={phoneNumber}
                            onChange={phoneChangeHandler}
                            onBlur={phoneBlurHandler}
                            error={phoneErrorMessage}
                            autoComplete="off"
                            type="tel"
                        />

                        <TextField
                            {...styles.textFieldStyles}
                            type="email"
                            label="Email address"
                            required={true}
                            placeholder=""
                            value={emailAddres}
                            onChange={emailChangeHandler}
                            onBlur={emailBlurHandler}
                            error={emailErrorMessage}
                        />
                    </StyledWrapper>
                    <StyledWrapper {...styles.formGroup}>
                        <Select
                            {...styles.locationSelect}
                            label={translate("Nearest Location")}
                            required={true}
                            value={selectedLocation || ""}
                            error={locationError}
                            onChange={event => listChangeHandler(event.target.value)}
                            data-test-selector="nearby_location_dropdown"
                        >
                            <option value="">{translate("Select Nearest Location")}</option>
                            {options.map(option => (
                                <option key={option.optionValue} value={option.optionValue}>
                                    {option.optionText}
                                </option>
                            ))}
                        </Select>                        
                    </StyledWrapper>

                    <TextArea
                        {...styles.messageTextArea}
                        label="Message"
                        required={false}
                        placeholder={messagePlaceholder}
                        value={message}
                        onChange={messageChangeHandler}
                    />
                    <UcsReCaptcha recaptchaRef={recaptchaRef} location="GuestQuoteRequest" />
                    <StyledWrapper {...styles.submitButtonWrapper}>
                        <Button
                            onClick={e => {
                                e.preventDefault();
                                validateForm();
                            }}
                            disabled={isSubmitting}
                        >
                            Send quote request
                        </Button>
                        <Button
                            style={{ marginLeft: "5px" }}
                            variant="secondary"
                            onClick={e => {
                                e.preventDefault();
                                modalCloseHandler();
                            }}
                        >
                            {translate("Cancel")}
                        </Button>
                    </StyledWrapper>
                </form>
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(GuestQuoteRequestForm));
